<template>
  <div>
    <ts-loading-banner :loading="loading">
      <div>
        <div class="row">
          <div class="col-md-6">
            <label class="required col-form-label">{{
              $t('dailyCashAdvance.advanceDate')
            }}</label>
            <a-date-picker
              v-model="model.advance_date"
              format="DD-MM-YYYY"
              :allowClear="false"
              valueFormat="DD-MM-YYYY"
              class="ts-w-full"
            ></a-date-picker>
            <div class="validate ts-text-xs" v-if="errors.has('advance_date')">
              {{ errors.first('advance_date') }}
            </div>
          </div>
          <div class="col-md-6">
            <label class="required col-form-label">{{
              $t('dailyCashAdvance.advancedBy')
            }}</label>
            <a-select
              v-model="model.advanced_by"
              :options="employees"
              showSearch
              :filterOption="
                (input, option) =>
                  option.componentOptions.children[0].text
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
              "
              class="ts-w-full"
            ></a-select>
            <div class="validate ts-text-xs" v-if="errors.has('advanced_by')">
              {{ errors.first('advanced_by') }}
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <label class="required col-form-label">{{
              $t('dailyCashAdvance.purpose')
            }}</label>
            <input
              v-model="model.purpose"
              type="text"
              class="form-control"
              style="height: 32px;"
              :class="{ 'is-invalid': errors.has('exp_cat_code') }"
            />
            <div class="validate ts-text-xs" v-if="errors.has('purpose')">
              {{ errors.first('purpose') }}
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <label class="col-form-label">{{
              $t('dailyCashAdvance.vendorName')
            }}</label>
            <a-select
              v-model="model.vendor_name"
              :options="vendors"
              allowClear
              show-search
              class="ts-w-full"
              @search="handleSearchVendor"
              :loading="vendor_searching"
              @change="() => (!model.vendor_name ? handleSearchVendor('') : '')"
            ></a-select>
          </div>
          <div class="col-md-6">
            <label class="required col-form-label">{{
              $t('dailyCashAdvance.advancedAmount')
            }}</label>
            <ts-currency-converter-input
              :input_amount.sync="model.input_advance_amount"
              :amount.sync="model.advance_amount"
              :input_currency_id.sync="model.input_currency_id"
              :currency_id.sync="model.currency_id"
              :fx_rate.sync="model.fx_rate"
              :fx_rate_ratio.sync="model.fx_rate_ratio"
              :show_convert="true"
            />
            <div
              class="validate ts-text-xs"
              v-if="errors.has('advance_amount')"
            >
              {{ errors.first('advance_amount') }}
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <fieldset>
              <legend class="text-blue ts-m-0">
                {{ $t('dailyCashAdvance.payment') }}
              </legend>
              <div class="row">
                <div class="col-md-6">
                  <label class="required col-form-label">{{
                    $t('dailyCashAdvance.paymentMethod')
                  }}</label>
                  <a-select
                    v-model="model.payment_method_id"
                    :options="paymentMethods"
                    showSearch
                    :filterOption="
                      (input, option) =>
                        option.componentOptions.children[0].text
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                    "
                    @change="onPaymentMethodChange"
                    class="ts-w-full"
                  ></a-select>
                  <div
                    class="validate ts-text-xs"
                    v-if="errors.has('payment_method_id')"
                  >
                    {{ errors.first('payment_method_id') }}
                  </div>
                </div>
                <div class="col-md-6">
                  <label
                    class="col-form-label"
                    :class="isBank ? '' : 'required'"
                    >{{ $t('dailyCashAdvance.bank') }}</label
                  >
                  <a-select
                    v-model="model.bank_id"
                    :options="bankProfiles"
                    :disabled="isBank"
                    showSearch
                    :filterOption="
                      (input, option) =>
                        option.componentOptions.children[0].text
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                    "
                    class="ts-w-full"
                  ></a-select>
                  <div class="validate ts-text-xs" v-if="errors.has('bank_id')">
                    {{ errors.first('bank_id') }}
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <label
                    class="col-form-label"
                    :class="isBank ? '' : 'required'"
                    >{{ $t('dailyCashAdvance.accountName') }}</label
                  >
                  <input
                    v-model="model.account_name"
                    type="text"
                    :disabled="isBank"
                    :placeholder="$t('dailyCashAdvance.accountName')"
                    class="form-control"
                    style="height: 32px;"
                    :class="{ 'is-invalid': errors.has('exp_cat_code') }"
                  />
                  <div
                    class="validate ts-text-xs"
                    v-if="errors.has('account_name')"
                  >
                    {{ errors.first('account_name') }}
                  </div>
                </div>
                <div class="col-md-6">
                  <label
                    class="col-form-label"
                    :class="isBank ? '' : 'required'"
                    >{{ $t('dailyCashAdvance.accountNumber') }}</label
                  >
                  <input
                    v-model="model.account_number"
                    type="text"
                    :disabled="isBank"
                    :placeholder="$t('dailyCashAdvance.accountNumber')"
                    class="form-control"
                    style="height: 32px;"
                    :class="{ 'is-invalid': errors.has('exp_cat_code') }"
                  />
                  <div
                    class="validate ts-text-xs"
                    v-if="errors.has('account_number')"
                  >
                    {{ errors.first('account_number') }}
                  </div>
                </div>
              </div>
            </fieldset>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <label class="col-form-label">{{
              $t('dailyCashAdvance.description')
            }}</label>
            <textarea
              rows="3"
              class="form-control"
              :placeholder="$t('dailyCashAdvance.description')"
              v-model.trim="model.description"
            ></textarea>
            <div class="validate ts-text-xs" v-if="errors.has('description')">
              {{ errors.first('description') }}
            </div>
          </div>
        </div>
        <div class="row ts-mt-3">
          <div class="col-md-12 ts-flex ts-justify-end ts-space-x-3">
            <ts-button @click="$emit('cancel')">{{ $t('cancel') }}</ts-button>
            <ts-button
              color="primary"
              outline
              v-if="!isUpdate"
              :waiting="waiting_new"
              @click.prevent="onSaveAddNew"
              >{{ $t('save_add_new') }}</ts-button
            >
            <ts-button
              v-if="!isUpdate"
              color="primary"
              :waiting="waiting"
              @click.prevent="onSave"
              >{{ $t('save') }}</ts-button
            >
            <ts-button
              v-if="isUpdate"
              color="primary"
              :waiting="waiting"
              @click.prevent="onUpdate"
              >{{ $t('update') }}</ts-button
            >
          </div>
        </div>
      </div>
    </ts-loading-banner>
  </div>
</template>

<script>
import moment from 'moment'
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'
import { debounce } from 'lodash'
export default {
  name: 'formCreatePaymentToVendor',
  data() {
    return {
      loading: false,
      waiting: false,
      waiting_new: false,
      vendor_searching: false,
      errors: new Errors(),
      model: {
        advance_id: null,
        advance_date: moment().format('DD-MM-YYYY'),
        currency_id: null,
        advance_amount: 0,
        amount_used: 0,
        amount_due: 0,
        purpose: null,
        description: null,
        fx_rate: null,
        vendor_name: null,
        advanced_by: null,
        payment_method_id: 1,
        bank_id: null,
        account_name: null,
        account_number: null,
        advance_status: 0,
        status_by: 0,
        input_advance_amount: 0,
        input_currency_id: null,
        fx_rate_ratio: null
      }
    }
  },
  async created() {
    await this.setEditData()
    await this.getPreData({
      params: {},
      index: undefined
    })
  },
  computed: {
    ...mapState('expense/dailyCashAdvance', ['formViewDatas', 'edit_data']),
    advanceStatus() {
      return [
        {
          label: 'Open',
          value: 0
        },
        {
          label: 'Processing',
          value: 1
        },
        {
          label: 'Reimbursed',
          value: 2
        },
        {
          label: 'Cancel',
          value: 3
        }
      ]
    },
    currency() {
      if (this.formViewDatas['currency']) {
        return this.formViewDatas['currency'].map(el => ({
          value: el.currency_id,
          label: el.currency_code,
          is_default: el.is_default
        }))
      }
      return []
    },
    paymentMethods() {
      if (this.formViewDatas['paymentMethods']) {
        return this.formViewDatas['paymentMethods'].map(el => ({
          value: el.payment_method_id,
          label: el.payment_method
        }))
      }
      return []
    },
    bankProfiles() {
      if (this.formViewDatas['bankProfiles']) {
        return this.formViewDatas['bankProfiles'].map(el => ({
          value: el.bank_id,
          label: el.bank_name
        }))
      }
      return []
    },
    employees() {
      if (this.formViewDatas['employees']) {
        return this.formViewDatas['employees'].map(el => ({
          value: el.employee_id,
          label:
            el.employee_name_en +
            (el.employee_name_kh ? '|' + el.employee_name_kh : ''),
          user_id: el.user_id
        }))
      }
      return []
    },
    vendors() {
      if (this.formViewDatas['vendors']) {
        return this.formViewDatas['vendors'].map(el => ({
          value: el.vendor_name,
          label: el.vendor_name + (el.type == 'new' ? ' (NEW)' : '')
        }))
      }
      return []
    },
    isUpdate() {
      return !_.isEmpty(this.edit_data)
    },
    isBank() {
      return this.model.payment_method_id == 1 ? true : false
    }
  },
  methods: {
    ...mapActions('expense/dailyCashAdvance', [
      'getFormViewModel',
      'store',
      'update'
    ]),
    handleSearchVendor: debounce(async function(value) {
      this.vendor_searching = true
      await this.getFormViewModel({
        index: 'vendors',
        params: {
          fnName: 'vendors',
          search_vendor: value
        }
      })
      if (
        !this.formViewDatas['vendors'].find(el => el.vendor_name == value) &&
        value
      ) {
        this.formViewDatas['vendors'].push({
          vendor_name: value,
          type: 'new'
        })
      }
      this.vendor_searching = false
    }, 500),
    async getPreData({ params, index }) {
      this.loading = true
      await this.getFormViewModel({ index: index, params: params })
        .then(() => {
          let default_currency = this.formViewDatas['currency'].find(
            el => el.is_default == true
          )

          if (!this.isUpdate) {
            this.model.input_currency_id = default_currency
              ? default_currency.currency_id
              : null

            this.model.currency_id = default_currency
              ? default_currency.currency_id
              : null

            this.model.fx_rate = 1

            this.model.fx_rate_ratio = 1
          }
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => (this.loading = false))
    },
    onPaymentMethodChange() {
      this.model.bank_id = null
      this.model.account_name = null
      this.model.account_number = null
    },
    onUpdate() {
      this.waiting = true
      this.update({
        id: this.model.advance_id,
        data: this.model
      })
        .then(response => {
          this.$toasted.success(response.message)
          this.$emit('cancel')
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    onSave() {
      this.waiting = true
      this.store(this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.$emit('cancel')
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    onSaveAddNew() {
      this.waiting_new = true
      this.store(this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.clearInput()
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting_new = false
        })
    },
    async clearInput() {
      this.model.advance_id = null
      this.model.advance_date = moment().format('DD-MM-YYYY')
      this.model.currency_id = null
      this.model.advance_amount = null
      this.model.amount_used = 0
      this.model.amount_due = 0
      this.model.purpose = null
      this.model.description = null
      this.model.fx_rate = null
      this.model.vendor_name = null
      this.model.advanced_by = null
      this.model.payment_method_id = 1
      this.model.bank_id = null
      this.model.account_name = null
      this.model.account_number = null
      this.model.advance_status = 1
      this.model.status_by = 1
      this.model.input_advance_amount = null
      this.model.input_currency_id = null
      this.model.fx_rate_ratio = null
    },
    setEditData() {
      if (!_.isEmpty(this.edit_data)) {
        this.model.advance_id = this.edit_data.advance_id
        this.model.advance_date = this.edit_data.advance_date
        this.model.currency_id = this.edit_data.currency_id
        this.model.advance_amount = this.edit_data.advance_amount
        this.model.amount_used = this.edit_data.amount_used
        this.model.amount_due = this.edit_data.amount_due
        this.model.purpose = this.edit_data.purpose
        this.model.description = this.edit_data.description
        this.model.fx_rate = this.edit_data.fx_rate
        this.model.vendor_name = this.edit_data.vendor_name
        this.model.advanced_by = this.edit_data.advanced_by
        this.model.payment_method_id = this.edit_data.payment_method_id
        this.model.bank_id = this.edit_data.bank_id
        this.model.account_name = this.edit_data.account_name
        this.model.account_number = this.edit_data.account_number
        this.model.advance_status = this.edit_data.advance_status
        this.model.status_by = this.edit_data.status_by
        this.model.input_advance_amount = this.edit_data.input_advance_amount
        this.model.input_currency_id = this.edit_data.input_currency_id
        this.model.fx_rate_ratio = this.edit_data.fx_rate_ratio
      } else {
        this.model.advanced_by = this.$store.state.authUser.user.employee.employee_id
      }
    }
  }
}
</script>
<style scoped>
/* .form-control {
  height: 32px;
} */
</style>
<style>
.ant-calendar-picker-input {
  height: 32px !important;
}
/* .ant-input-group-addon .ant-select .ant-select-selection {
  margin: 0;
}
.ant-input-group .ant-input {
  height: 32px !important;
} */
</style>
